import { MenuItem } from "features/dashboard/dashboard-menu/DashboardMenuItem";
import { AbpPolicies } from "core/constants/AbpPolicies";
import { FileIcon, TagIcon, Users, User, Workflow, FileSignature, HomeIcon, ClipboardList, CreditCard, ScrollText, FileText, Calculator, Building, Building2, FileDown, LucideIcon, Network, TrendingUp, FolderKanban, BarChart3, Briefcase, Database, ShieldCheck, Landmark, LogIn } from "lucide-react";

/** Groupe du menu BO desktop : soit un lien direct (to), soit un panneau (items). */
export interface BoMenuGroup {
  id: string;
  label: string;
  icon: LucideIcon;
  /** Lien direct (pas de flyout). */
  to?: string;
  /** Entrées du flyout affiché au survol. */
  items?: MenuItem[];
  /** Permission ABP requise pour un lien direct (les entrées portent la leur). */
  permission?: string;
}

/**
 * Menu BO desktop : groupes logiques avec flyout au survol (fini la longue liste
 * scrollable). Le flat boMenuItems ci-dessous reste utilisé par le menu mobile.
 */
export const boMenuGroups: BoMenuGroup[] = [
  {
    id: 'home',
    label: 'SIDEBAR.HOME',
    icon: HomeIcon,
    to: '/back-office/home'
  },
  {
    id: 'statistics',
    label: 'Statistiques',
    icon: BarChart3,
    to: '/back-office/statistiques'
  },
  {
    id: 'acteurs',
    label: 'Acteurs',
    icon: Users,
    items: [
      { id: 'personnes-morales', label: 'Personnes morales', icon: Building, to: '/back-office/acteurs/personnes-morales' },
      { id: 'compagnies', label: 'Compagnies', icon: Landmark, to: '/back-office/acteurs/compagnies' },
      { id: 'brokers', label: 'Courtiers', icon: User, to: '/back-office/acteurs/courtiers', permission: AbpPolicies.ADMIN_COURTIERS_READ },
      { id: 'mias', label: 'MIA', icon: Briefcase, to: '/back-office/acteurs/mias', permission: AbpPolicies.MIAS_READ },
      { id: 'pims', label: 'Prof. immobilier', icon: Building2, to: '/back-office/acteurs/pims', permission: AbpPolicies.PIMS_READ },
      { id: 'clients', label: 'Clients', icon: User, to: '/back-office/acteurs/clients', permission: AbpPolicies.ADMIN_CLIENTS_READ }
    ]
  },
  {
    // La plateforme est un espace de distribution, pas un acteur : entrée dédiée.
    id: 'plateformes',
    label: 'Plateformes',
    icon: Network,
    to: '/back-office/plateformes'
  },
  {
    id: 'activite',
    label: 'Activité',
    icon: FolderKanban,
    items: [
      { id: 'simulations', label: 'Simulations', icon: Calculator, to: '/back-office/simulations', permission: AbpPolicies.ADMIN_SIMULATIONS_READ },
      { id: 'quotes', label: 'Devis', icon: FileText, to: '/back-office/devis', permission: AbpPolicies.ADMIN_DEVIS_READ },
      { id: 'contracts', label: 'Contrats', icon: ScrollText, to: '/back-office/contrats', permission: AbpPolicies.ADMIN_CONTRATS_READ },
      { id: 'group-contract', label: 'Contrats collectifs', icon: FileSignature, to: '/back-office/contrats-groupe' }
    ]
  },
  {
    id: 'catalogue',
    label: 'Catalogue',
    icon: TagIcon,
    items: [
      { id: 'products', label: 'Produits', icon: TagIcon, to: '/back-office/produits', permission: AbpPolicies.ADMIN_PRODUITS_READ },
      { id: 'tunnels', label: 'Tunnels', icon: Workflow, to: '/back-office/tunnels', permission: AbpPolicies.ADMIN_TUNNELS_READ },
      { id: 'branches', label: 'Branches', icon: Network, to: '/back-office/branches' },
      { id: 'indices', label: 'Indices', icon: TrendingUp, to: '/back-office/indices' },
      { id: 'surveys', label: 'Questionnaires', icon: ClipboardList, to: '/back-office/questionnaires' },
      { id: 'copro-registry', label: 'Registre des copropriétés', icon: Database, to: '/back-office/registre-coproprietes', permission: AbpPolicies.JOBS_MANAGER }
    ]
  },
  {
    id: 'prelevements',
    label: 'Prélèvements',
    icon: CreditCard,
    items: [
      { id: 'payment-schedule-all', label: 'Tous les prélèvements', icon: CreditCard, to: '/back-office/prelevements', permission: AbpPolicies.ADMIN_PRELEVEMENTS_READ },
      { id: 'payment-schedule-hbs', label: 'Prélèvements HBS', icon: FileDown, to: '/back-office/prelevements/hbs', permission: AbpPolicies.PRELEVEMENTS_HBS_READ },
      { id: 'payment-schedule-members', label: 'Aliments des collectifs', icon: FileSignature, to: '/back-office/prelevements/aliments' }
    ]
  },
  {
    id: 'documents',
    label: 'Documents',
    icon: FileIcon,
    to: '/back-office/documents'
  },
  {
    id: 'atelier-roles',
    label: 'Gestion des rôles',
    icon: ShieldCheck,
    to: '/back-office/atelier-roles',
    permission: AbpPolicies.ATELIER_ROLES
  },
  {
    id: 'connexions',
    label: 'Connexions',
    icon: LogIn,
    to: '/back-office/admin/connexions',
    permission: AbpPolicies.ADMIN_CONNEXIONS_READ
  }
];

export const boMenuItems: MenuItem[] = [
  {
    id: 'home',
    label: 'SIDEBAR.HOME',
    icon: HomeIcon,
    to: '/back-office/home'
  },
  {
    id: 'documents',
    label: 'SIDEBAR.DOCUMENTS_MANAGER',
    icon: FileIcon,
    to: '/back-office/documents'
  },
  {
    id: 'persons',
    label: 'SIDEBAR.PERSONS_MANAGER',
    icon: Users,
    children: [
      {
        id: 'personnes-morales',
        label: 'Personnes morales',
        icon: Building,
        to: '/back-office/acteurs/personnes-morales'
      },
      {
        id: 'compagnies',
        label: 'Compagnies',
        icon: Landmark,
        to: '/back-office/acteurs/compagnies'
      },
      {
        id: 'brokers',
        label: 'SIDEBAR.BROKERS',
        icon: User,
        to: '/back-office/acteurs/courtiers',
        permission: AbpPolicies.ADMIN_COURTIERS_READ
      },
      {
        id: 'clients',
        label: 'SIDEBAR.CLIENTS',
        icon: User,
        to: '/back-office/acteurs/clients',
        permission: AbpPolicies.ADMIN_CLIENTS_READ
      },
    ]
  },
  {
    id: 'plateformes',
    label: 'SIDEBAR.ORGANIZATIONS',
    icon: User,
    to: '/back-office/plateformes'
  },
  {
    id: 'products',
    label: 'SIDEBAR.PRODUCTS_MANAGER',
    icon: TagIcon,
    to: '/back-office/produits'
  },
  {
    id: 'tunnels',
    label: 'SIDEBAR.FUNNELS_MANAGER',
    icon: Workflow,
    to: '/back-office/tunnels'
  },
  {
    id: 'branches',
    label: 'Branches',
    icon: Network,
    to: '/back-office/branches'
  },
  {
    id: 'indices',
    label: 'Indices',
    icon: TrendingUp,
    to: '/back-office/indices'
  },
  {
    id: 'group-contract',
    label: 'SIDEBAR.GROUP_CONTRACT_MANAGER',
    icon: FileSignature,
    to: '/back-office/contrats-groupe'
  },
  {
    id: 'surveys',
    label: 'SIDEBAR.SURVEYS_MANAGER',
    icon: ClipboardList,
    to: '/back-office/questionnaires'
  },
  {
    id: 'payment-schedule',
    label: 'SIDEBAR.PAYMENT_SCHEDULE_MANAGER',
    icon: CreditCard,
    children: [
      {
        id: 'payment-schedule-all',
        label: 'Tous les prélèvements',
        icon: CreditCard,
        to: '/back-office/prelevements'
      },
      {
        id: 'payment-schedule-hbs',
        label: 'Prélèvements HBS',
        icon: FileDown,
        to: '/back-office/prelevements/hbs',
        permission: AbpPolicies.PRELEVEMENTS_HBS_READ
      },
      {
        id: 'payment-schedule-members',
        label: 'Aliments des collectifs',
        icon: FileSignature,
        to: '/back-office/prelevements/aliments'
      }
    ]
  },
  {
    id: 'contracts',
    label: 'SIDEBAR.CONTRACTS_MANAGER',
    icon: ScrollText,
    to: '/back-office/contrats',
    // Les listes BO utilisent les routes /admin/liste, protégées par les permissions Admin*
    permission: AbpPolicies.ADMIN_CONTRATS_READ
  },
  {
    id: 'quotes',
    label: 'SIDEBAR.QUOTES_MANAGER',
    icon: FileText,
    to: '/back-office/devis',
    permission: AbpPolicies.ADMIN_DEVIS_READ
  },
  {
    id: 'simulations',
    label: 'SIDEBAR.SIMULATIONS_MANAGER',
    icon: Calculator,
    to: '/back-office/simulations',
    permission: AbpPolicies.ADMIN_SIMULATIONS_READ
  },
  {
    id: 'connexions',
    label: 'Connexions',
    icon: LogIn,
    to: '/back-office/admin/connexions',
    permission: AbpPolicies.ADMIN_CONNEXIONS_READ
  }
];
